<template>
  <div class="dppl-dark-tooltip">
    <div ref="reference" class="activator">
      <slot
        name="activator"
        :on-mouseenter="hideTooltip ? () => {} : handleActivatorMouseEnter"
        :on-mouseleave="hideTooltip ? () => {} : handleActivatorMouseLeave"
      />
    </div>
    <div
      v-show="isTooltipVisible"
      ref="floating"
      :class="['tooltip', colorClass]"
      @mouseenter="isHoveringContent = true"
      @mouseleave="handleContentMouseLeave"
    >
      <div>
        <div
          ref="floatingArrow"
          class="arrow"
        />
        <slot name="tooltip-content" />
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computePosition, arrow, offset, autoUpdate,
} from '@floating-ui/dom';
import {
  computed, ref, useTemplateRef,
} from 'vue';

const props = defineProps({
  hideTooltip: {
    type: Boolean,
    default: false,
  },
  tooltipOffset: {
    type: Number,
    default: 0,
  },
  multipleTooltipOffsets: {
    type: Object,
    default: () => ({}),
  },
  maxWidth: {
    type: String,
    default: '252px',
  },
  lightMode: {
    type: Boolean,
    default: false,
  },
  tooltipPlacement: {
    type: String,
    default: 'bottom',
  },
});

const isTooltipVisible = ref(false);
const isHoveringContent = ref(false);
const isHoveringActivator = ref(false);
const activatorTimeout = ref(null);
const contentTimeout = ref(null);

const reference = useTemplateRef('reference');
const floating = useTemplateRef('floating');
const floatingArrow = useTemplateRef('floatingArrow');
const placementRef = ref(props.tooltipPlacement || 'bottom');

const middleware = [];

if (props.tooltipOffset) {
  middleware.push(offset(props.tooltipOffset));
} else if (
  props.multipleTooltipOffsets?.mainAxis
  || props.multipleTooltipOffsets?.crossAxis
  || props.multipleTooltipOffsets?.alignmentAxis
) {
  middleware.push(offset(props.multipleTooltipOffsets));
}

// Computed
const colorClass = computed(() => (props.lightMode ? 'light' : 'dark'));

// Methods
const updateTooltip = async () => {
  const {
    x,
    y,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } },
  } = await computePosition(reference.value, floating.value, {
    whileElementsMounted: autoUpdate,
    placement: placementRef.value,
    middleware: [
      ...middleware,
      arrow({ element: floatingArrow.value }),
    ],
  });

  Object.assign(floating.value.style, {
    left: `${x}px`,
    top: `${y}px`,
  });

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  Object.assign(floatingArrow.value.style, {
    left: arrowX != null ? `${arrowX}px` : '',
    top: arrowY != null ? `${arrowY}px` : '',
    right: '',
    bottom: '',
    [staticSide]: '-4px',
  });
};

const handleActivatorMouseEnter = () => {
  isHoveringActivator.value = true;
  if (!props.hideTooltip) {
    isTooltipVisible.value = true;

    updateTooltip();
  }
};

const handleActivatorMouseLeave = () => {
  isHoveringActivator.value = false;
  clearTimeout(contentTimeout.value);
  activatorTimeout.value = setTimeout(() => {
    if (!isHoveringContent.value) {
      isTooltipVisible.value = false;
    }
  }, 100);
};

const handleContentMouseLeave = () => {
  isHoveringContent.value = false;
  clearTimeout(activatorTimeout.value);
  contentTimeout.value = setTimeout(() => {
    if (!isHoveringActivator.value) {
      isTooltipVisible.value = false;
    }
  }, 100);
};
</script>

<style scoped lang="less">
.dppl-dark-tooltip {
  display: inherit;
}

.activator {
  width: 100%;
}

.tooltip {
  position: absolute;
  top: 0;
  left: 0;
  max-width: v-bind(maxWidth);
  z-index: 4;
  background-color: @text-darkest;
  color: @white;
  padding: 16px;
  border-radius: 8px;
  text-align: initial;

  .arrow {
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: @text-darkest;
    transform: rotate(45deg);
  }

  // light mode and new design
  &.light {
    background-color: @white;
    padding: 10px;
    box-shadow: 0 0 4px 0 @grey-400;
    color: @text-darkest;
    border-radius: 4px;

    .arrow {
      background-color: @white;
    }
  }
}
</style>
